import {
	AlertCircle,
	AlertTriangle,
	CheckCircle,
	Info,
	type LucideIcon,
	XCircle
} from 'lucide-react';
import type React from 'react';
import { cn } from '@/lib/utils';

type AlertVariant =
	| 'primary'
	| 'secondary'
	| 'success'
	| 'danger'
	| 'warning'
	| 'info'
	| 'light'
	| 'dark';

interface AlertProps {
	variant?: AlertVariant;
	className?: string;
	testId?: string;
	/** @deprecated Use icon prop instead - iconClassName with FontAwesome classes won't work in Shadow DOM */
	iconClassName?: string;
	/** Custom Lucide icon component. If not provided, uses default icon based on variant */
	icon?: LucideIcon;
	/** Show the default icon for the variant */
	showIcon?: boolean;
	text?: string;
	children?: React.ReactNode;
}

const variantStyles: Record<AlertVariant, string> = {
	primary: 'bg-primary/10 border-primary/50 text-primary',
	secondary: 'bg-secondary/10 border-secondary/50 text-secondary-foreground',
	success: 'bg-green-50 border-green-500/50 text-green-900',
	danger: 'bg-destructive/10 border-destructive/50 text-destructive',
	warning: 'bg-yellow-50 border-yellow-500/50 text-yellow-900',
	info: 'bg-blue-50 border-blue-500/50 text-blue-900',
	light: 'bg-gray-100 border-gray-300 text-gray-900',
	dark: 'bg-gray-900 border-gray-700 text-gray-100'
};

const variantIcons: Record<AlertVariant, LucideIcon> = {
	primary: Info,
	secondary: Info,
	success: CheckCircle,
	danger: XCircle,
	warning: AlertTriangle,
	info: AlertCircle,
	light: Info,
	dark: Info
};

export default function Alert({
	variant = 'info',
	className = '',
	testId,
	iconClassName,
	icon,
	showIcon = false,
	text,
	children
}: AlertProps): React.JSX.Element {
	const baseStyles = 'rounded-lg border p-4';
	const variantStyle = variantStyles[variant];

	// Determine which icon to show: custom icon > iconClassName triggers default > showIcon
	const shouldShowIcon = icon || iconClassName || showIcon;
	const IconComponent = icon || variantIcons[variant];

	return (
		<div className={cn(baseStyles, variantStyle, className)} role="alert" data-testid={testId}>
			<div className="flex items-start">
				{shouldShowIcon && (
					<div className="shrink-0 mr-3">
						<IconComponent className="h-5 w-5" />
					</div>
				)}
				<span className="flex-1">{text ?? children}</span>
			</div>
		</div>
	);
}
